import { api, type FileRules, type MessageFileView } from '../api/client';

/**
 * Files in Messages over 25 MB (to 500 MB, 1.12.16): sent in parts straight into Stitch Block
 * Storage, two at a time, each tried up to three times; then put together and checked for viruses
 * like any other. Cancelling stops it and Stitch throws the parts away.
 */

/** What Messages takes, per company (asked once a page visit). */
const rules = new Map<string, Promise<FileRules>>();
const FALLBACK: FileRules = { endings: [], maxBytes: 25 * 1024 * 1024, partBytes: 16 * 1024 * 1024, checked: false };

export function fileRules(domain: string): Promise<FileRules> {
  const key = domain.toLowerCase();
  let r = rules.get(key);
  if (!r) {
    r = api.fileRules(domain).catch(() => {
      rules.delete(key);
      return FALLBACK;
    });
    rules.set(key, r);
  }
  return r;
}

/** One go, or in parts. */
export const ONE_GO_BYTES = 25 * 1024 * 1024;

/** "500 MB". */
export function sizeWords(bytes: number): string {
  return bytes >= 1024 * 1024 * 1024 ? `${Math.round(bytes / (1024 * 1024 * 1024))} GB` : `${Math.round(bytes / (1024 * 1024))} MB`;
}

export interface UploadHandle {
  /** Stops it (and Stitch throws away what arrived). */
  cancel: () => void;
  done: Promise<MessageFileView>;
}

export class UploadCancelled extends Error {
  constructor() {
    super('Cancelled');
  }
}

/** Sends a big file in parts; `progress` hears 0 to 1 as parts arrive. */
export function uploadInParts(domain: string, conversationId: string, file: File, partBytes: number, progress: (share: number) => void): UploadHandle {
  let stopped = false;
  let uploadId: string | null = null;
  const done = (async () => {
    const started = await api.startUpload(domain, conversationId, file.name, file.size);
    uploadId = started.upload.id;
    const size = started.upload.partBytes || partBytes;
    const parts = started.upload.parts;
    let next = 1;
    let sent = 0;
    const one = async (part: number) => {
      const blob = file.slice((part - 1) * size, Math.min(file.size, part * size));
      for (let attempt = 1; ; attempt++) {
        if (stopped) throw new UploadCancelled();
        try {
          await api.putUploadPart(domain, conversationId, started.upload.id, part, blob);
          sent += blob.size;
          progress(Math.min(0.99, sent / file.size));
          return;
        } catch (err) {
          if (attempt >= 3 || stopped) throw err;
          await new Promise((r) => setTimeout(r, 1000 * attempt));
        }
      }
    };
    const worker = async () => {
      while (next <= parts) {
        const part = next++;
        await one(part);
      }
    };
    await Promise.all([worker(), worker()]);
    if (stopped) throw new UploadCancelled();
    const r = await api.finishUpload(domain, conversationId, started.upload.id);
    progress(1);
    return r.file;
  })();
  done.catch(() => {
    // A failed or cancelled upload: Stitch throws its parts away (and sweeps any left after a day).
    if (uploadId) void api.cancelUpload(domain, conversationId, uploadId).catch(() => undefined);
  });
  return {
    cancel: () => {
      stopped = true;
    },
    done
  };
}
